/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { EnduranceRbacRolesEnums } from '../enums/api/endurance-rbac-roles.enums';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable } from 'rxjs';
import { RbacAddRoleModel } from '../models/rbac-addRole.model';
import { RbacDeleteRoleByIdModel } from '../models/rbac-deleteRoleById.model';
import { RbacGetRoleByIdModel } from '../models/rbac-getRoleById.model';

@Injectable()
export class EnduranceRBACRolesApiService {
    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Create a role using the RBAC system on the API.
     * @param {string} roleObject
     * @returns {any}
     */
    public editRole(roleObject: any): Observable<RbacAddRoleModel[]> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.put(
                EnduranceRbacRolesEnums.PUT_ROLE_EDIT(roleObject.id),
                roleObject,
                moduleAuth,
                false
            ).subscribe(
                data => {
                    observer.next(data);
                    observer.complete();
                },
                error => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Create a role using the RBAC system on the API.
     * @param {string} roleObject
     * @returns {any}
     */
    public createRole(roleObject: any): Observable<RbacAddRoleModel[]> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.post(
                EnduranceRbacRolesEnums.POST_ROLES_ADD,
                roleObject,
                moduleAuth,
                false
            ).subscribe(
                data => {
                    observer.next(data);
                    observer.complete();
                },
                error => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Delete a role using the RBAC system on the API.
     * @param {string} roleId
     * @returns {any}
     */
    public deleteRole(roleId: string): Observable<RbacDeleteRoleByIdModel[]> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.delete(
                EnduranceRbacRolesEnums.DELETE_ROLE_ID(roleId),
                moduleAuth,
                false
            ).subscribe(
                data => {
                    observer.next(data);
                    observer.complete();
                },
                error => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Get all role using the RBAC system on the API.
     * @returns {any}
     */
    public getAllRoles(): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.get(
                EnduranceRbacRolesEnums.GET_ROLES_ALL,
                moduleAuth,
                false
            ).subscribe(
                data => {
                    observer.next(data);
                    observer.complete();
                },
                error => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Get a role using the RBAC system on the API.
     * @param {string} roleId
     * @returns {any}
     */
    public getRoleById(roleId: string): Observable<RbacGetRoleByIdModel[]> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.get(
                EnduranceRbacRolesEnums.GET_ROLE_ID(roleId),
                moduleAuth,
                false
            ).subscribe(
                data => {
                    observer.next(data);
                    observer.complete();
                },
                error => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Get groups names.
     * @param groups
     * @param data
     */
    public getRoleName(roles, data): string {
        const roleNames = [];
        for (const role of roles) {
            if (role.id === data) {
                roleNames.push(role.name);
            }
        }

        return roleNames.join(', ');
    }
}
